import { DealerState } from '@appTypes/DealerState';
import { useCheckDevice } from '@hooks/UseCheckDevice';
import { usePwa } from '@hooks/UsePwa';
import { AppState } from '@store/AppState';
import { Dispatch, FC, SetStateAction, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useUserProfileForm } from '../../../../hooks/header/UseUserProfileForm';
import { UserProfileFormResource } from '../../../../types/header/UserProfile';
import RequestMobileAccess from './RequestMobileAccess';
import UserNotificationsPreference from './UserNotificationsPreference';
import UserProfileCarFax from './UserProfileCarFax';
import UserProfileInfo from './UserProfileInfo';
import UserProfilePassword from './UserProfilePassword';
import UserProfilePhoneNumber from './UserProfilePhoneNumber';
import UserProfilePreference from './UserProfilePreference';

type UserProfileFormProps = {
  formData: UserProfileFormResource;
  showChangePassword: boolean;
  submitted: boolean;
  onClose: () => void;
  setIsSaving: Dispatch<SetStateAction<boolean>>;
  setSubmitted: Dispatch<SetStateAction<boolean>>;
  setShowChangePassword: Dispatch<SetStateAction<boolean>>;
  setIsDirty?: Dispatch<SetStateAction<boolean>>;
  passwordValidation?: boolean;
  setPasswordValidation?: Dispatch<SetStateAction<boolean>>;
};

const UserProfileForm: FC<UserProfileFormProps> = (props) => {
  const { featureToggle, storeServices } = useSelector<AppState, DealerState>(
    (state) => state.dealer,
  );
  const mobileNotifications = featureToggle?.mobileNotifications;
  const mobileEnabled = featureToggle?.mobileEnabled;
  const {
    formData,
    showChangePassword,
    submitted,
    setIsSaving,
    setShowChangePassword,
    setSubmitted,
    onClose,
    setIsDirty,
  } = props;

  const [isOtpSubmittedAndVerified, setIsOtpSubmittedAndVerified] = useState(false);
  const [displayPhoneNumber, setDisplayPhoneNumber] = useState('');
  const {
    displayFirstName,
    displayLastName,
    displayUserName,
    isEssentialType,
    isIncentiveAdvantageType,
    isPrimaryUser,
    invalidPassword,
    passwordValidationResponse,
    currentPasswordIncorrect,
    showChangePasswordToggle,
    onSaveClick,
    setInvalidPassword,
    setPasswordValidationResponse,
    setCurrentPasswordIncorrect,
    setIsPasswordChanged,
  } = useUserProfileForm(
    formData,
    showChangePassword,
    onClose,
    setIsSaving,
    setSubmitted,
    setShowChangePassword,
    isOtpSubmittedAndVerified,
    displayPhoneNumber,
  );

  const { reset, formState, getValues } = useFormContext<UserProfileFormResource>();
  const { isMobileDevice } = useCheckDevice();
  const { isPwa, checkIfUserHasSingleDealerGroupAccess } = usePwa();
  const isSharedDevice = localStorage.getItem('isSharedPwaDevice');
  const hasMobileOffIpAccess = storeServices && storeServices.hasMobileOffIpAccess;
  const [hasSingleDealerGroupAccess, setHasSingleDealerGroupAccess] = useState<boolean>(false);

  useEffect(() => {
    checkIfUserHasSingleDealerGroupAccess().then((hasAccess: boolean) => {
      setHasSingleDealerGroupAccess(hasAccess);
    });
  }, []);

  useEffect(() => {
    if (showChangePassword || props.passwordValidation) {
      reset({ ...getValues(), currentPassword: '', password: '', confirmPassword: '' });
      setPasswordValidationResponse(null);
      setSubmitted(false);
      setInvalidPassword(false);
      props.setPasswordValidation(false);
    }
    if (props.passwordValidation) {
      reset({
        ...getValues(),
        currentPassword: '',
        serviceArrivalEmail: formData.serviceArrivalEmail,
        realtimeServiceArrivalEmail: formData.realtimeServiceArrivalEmail,
      });
      setIsPasswordChanged(false);
    }
  }, [showChangePassword, props.passwordValidation]);

  useEffect(() => {
    if (submitted) {
      onSaveClick();
    }
  }, [submitted]);

  useEffect(() => {
    if (formState.isDirty) {
      setIsDirty(formState.isDirty);
    }
  }, [formState.isDirty]);

  return (
    <>
      <UserProfileInfo
        displayFirstName={displayFirstName}
        displayLastName={displayLastName}
        displayUserName={displayUserName}
        isPrimaryUser={isPrimaryUser}
        isIncentiveAdvantageType={isIncentiveAdvantageType}
        showChangePassword={showChangePassword}
        showChangePasswordToggle={showChangePasswordToggle}
      />
      <UserProfileCarFax />
      {!isPrimaryUser && (
        <UserProfilePhoneNumber
          isOtpSubmittedAndVerified={isOtpSubmittedAndVerified}
          setIsOtpSubmittedAndVerified={setIsOtpSubmittedAndVerified}
          displayPhoneNumber={displayPhoneNumber}
          setDisplayPhoneNumber={setDisplayPhoneNumber}
        />
      )}
      <UserProfilePassword
        isIncentiveAdvantageType={isIncentiveAdvantageType}
        isPrimaryUser={isPrimaryUser}
        showChangePassword={showChangePassword}
        showChangePasswordToggle={showChangePasswordToggle}
        invalidPassword={invalidPassword}
        submitted={submitted}
        passwordValidationResponse={passwordValidationResponse}
        currentPasswordIncorrect={currentPasswordIncorrect}
        setCurrentPasswordIncorrect={setCurrentPasswordIncorrect}
        setPasswordValidationResponse={setPasswordValidationResponse}
      />
      <UserProfilePreference
        isPrimaryUser={isPrimaryUser}
        isIncentiveAdvantageType={isIncentiveAdvantageType}
        isEssentialType={isEssentialType}
      />
      {mobileNotifications && !isPrimaryUser && <UserNotificationsPreference />}

      {mobileEnabled &&
        hasMobileOffIpAccess &&
        hasSingleDealerGroupAccess &&
        isMobileDevice &&
        isPwa &&
        !isPrimaryUser &&
        isSharedDevice === 'false' && <RequestMobileAccess />}
    </>
  );
};

export default UserProfileForm;
